import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Bell, BellOff, BellRing, Check, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Convert base64 string to Uint8Array for applicationServerKey
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const PushNotificationManager = ({ showButton = true, onStatusChange }) => {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [vapidPublicKey, setVapidPublicKey] = useState(null);

  // Check if push notifications are supported
  useEffect(() => {
    const checkSupport = async () => {
      console.log('[PushNotification] Checking browser support...');
      const supported = 'serviceWorker' in navigator && 
                       'PushManager' in window && 
                       'Notification' in window;
      setIsSupported(supported);
      console.log('[PushNotification] Browser support:', supported);
      
      if (supported) {
        const currentPermission = Notification.permission;
        setPermission(currentPermission);
        console.log('[PushNotification] Current permission:', currentPermission);
        
        // Fetch VAPID key first (doesn't require auth)
        await fetchVapidKey();
        
        // Then check subscription status (requires auth)
        await checkSubscriptionStatus();
      }
      setIsLoading(false);
      console.log('[PushNotification] Initial check complete');
    };
    
    checkSupport();
  }, []);

  // Fetch VAPID public key
  const fetchVapidKey = async () => {
    try {
      console.log('[PushNotification] Fetching VAPID public key...');
      const response = await axios.get(`${API}/push/vapid-public-key`);
      setVapidPublicKey(response.data.publicKey);
      console.log('[PushNotification] VAPID key loaded successfully');
    } catch (error) {
      console.error('[PushNotification] Failed to fetch VAPID key:', error);
    }
  };

  // Check current subscription status
  const checkSubscriptionStatus = async () => {
    try {
      console.log('[PushNotification] Checking subscription status...');
      const response = await axios.get(`${API}/push/status`);
      const isCurrentlySubscribed = response.data.subscribed;
      setIsSubscribed(isCurrentlySubscribed);
      onStatusChange?.(isCurrentlySubscribed);
      console.log('[PushNotification] Subscription status:', isCurrentlySubscribed);
    } catch (error) {
      console.error('[PushNotification] Failed to check push status:', error);
      // This might fail if not authenticated, but that's okay
      // The component will still work for subscribing
    }
  };

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    setIsLoading(true);
    console.log('[PushNotification] Starting subscription process...');
    
    try {
      // Ensure VAPID key is loaded
      let publicKey = vapidPublicKey;
      if (!publicKey) {
        console.log('[PushNotification] VAPID key not loaded, fetching...');
        try {
          const response = await axios.get(`${API}/push/vapid-public-key`);
          publicKey = response.data.publicKey;
          setVapidPublicKey(publicKey);
          console.log('[PushNotification] VAPID key fetched successfully');
        } catch (keyError) {
          console.error('[PushNotification] VAPID fetch error:', keyError);
          toast.error('Server connection failed');
          return;
        }
      }
      
      if (!publicKey) {
        console.error('[PushNotification] No VAPID key available');
        toast.error('VAPID key unavailable');
        return;
      }
      
      if (!isSupported) {
        console.error('[PushNotification] Push not supported in browser');
        toast.error('Push not supported');
        return;
      }

      // Request notification permission
      console.log('[PushNotification] Requesting permission...');
      let permissionResult;
      try {
        permissionResult = await Notification.requestPermission();
        setPermission(permissionResult);
        console.log('[PushNotification] Permission result:', permissionResult);
      } catch (permError) {
        console.error('[PushNotification] Permission error:', permError);
        toast.error('Permission request failed');
        return;
      }

      if (permissionResult !== 'granted') {
        console.log('[PushNotification] Permission denied by user');
        toast.error('Permission denied');
        return;
      }

      // Get service worker with timeout
      console.log('[PushNotification] Getting service worker...');
      let registration;
      try {
        // First ensure service worker is registered
        if (!navigator.serviceWorker.controller) {
          console.log('[PushNotification] No active SW, registering...');
          registration = await navigator.serviceWorker.register('/service-worker.js');
          console.log('[PushNotification] SW registered, waiting for activation...');
          // Wait for it to be ready
          await navigator.serviceWorker.ready;
        } else {
          registration = await navigator.serviceWorker.ready;
        }
        console.log('[PushNotification] Service worker ready, scope:', registration.scope);
      } catch (swError) {
        console.error('[PushNotification] Service worker error:', swError);
        toast.error('Service worker unavailable');
        return;
      }
      
      // Remove existing subscription if any
      try {
        const existingSub = await registration.pushManager.getSubscription();
        if (existingSub) {
          console.log('[PushNotification] Removing old subscription...');
          await existingSub.unsubscribe();
        }
      } catch (e) {
        console.log('[PushNotification] Could not check existing subscription:', e);
      }
      
      // Subscribe to push
      console.log('[PushNotification] Creating push subscription with VAPID key...');
      let subscription;
      try {
        const applicationServerKey = urlBase64ToUint8Array(publicKey);
        console.log('[PushNotification] Application server key created, length:', applicationServerKey.length);
        
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey
        });
        console.log('[PushNotification] Push subscription created successfully');
        console.log('[PushNotification] Endpoint:', subscription.endpoint);
      } catch (subError) {
        console.error('[PushNotification] Subscribe error:', subError);
        console.error('[PushNotification] Error name:', subError.name);
        console.error('[PushNotification] Error message:', subError.message);
        toast.error('Push subscribe failed: ' + subError.message);
        return;
      }

      // Prepare subscription data for server
      const p256dhKey = subscription.getKey('p256dh');
      const authKey = subscription.getKey('auth');
      
      if (!p256dhKey || !authKey) {
        console.error('[PushNotification] Missing subscription keys');
        toast.error('Invalid subscription keys');
        return;
      }
      
      const subscriptionData = {
        subscription: {
          endpoint: subscription.endpoint,
          keys: {
            p256dh: btoa(String.fromCharCode(...new Uint8Array(p256dhKey))),
            auth: btoa(String.fromCharCode(...new Uint8Array(authKey)))
          }
        }
      };
      
      console.log('[PushNotification] Saving subscription to server...');
      console.log('[PushNotification] Subscription data:', JSON.stringify(subscriptionData, null, 2));
      
      try {
        const response = await axios.post(`${API}/push/subscribe`, subscriptionData);
        console.log('[PushNotification] Server response:', response.data);
      } catch (saveError) {
        console.error('[PushNotification] Save error:', saveError);
        console.error('[PushNotification] Save error response:', saveError.response?.data);
        console.error('[PushNotification] Save error status:', saveError.response?.status);
        toast.error('Failed to save subscription: ' + (saveError.response?.data?.detail || saveError.message));
        return;
      }

      setIsSubscribed(true);
      onStatusChange?.(true);
      toast.success('Push notifications enabled!');
      console.log('[PushNotification] Subscription complete!');
    } catch (error) {
      console.error('[PushNotification] Unexpected error:', error);
      toast.error('Error: ' + (error.message || 'Unknown'));
    } finally {
      setIsLoading(false);
      console.log('[PushNotification] Process finished, loading set to false');
    }
  }, [isSupported, vapidPublicKey, onStatusChange]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    setIsLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
      }
      
      await axios.delete(`${API}/push/unsubscribe`);
      
      setIsSubscribed(false);
      onStatusChange?.(false);
      toast.success('Push notifications disabled');
    } catch (error) {
      console.error('Failed to unsubscribe:', error);
      toast.error('Failed to disable push notifications');
    } finally {
      setIsLoading(false);
    }
  }, [onStatusChange]);

  // Send test notification
  const sendTestNotification = async () => {
    try {
      await axios.post(`${API}/push/test`);
      toast.success('Test notification sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test notification');
    }
  };

  if (!showButton) {
    return null;
  }

  if (!isSupported) {
    return (
      <Button variant="outline" disabled className="gap-2 opacity-50">
        <BellOff className="w-4 h-4" />
        <span className="hidden sm:inline">Not Supported</span>
      </Button>
    );
  }

  if (isLoading) {
    return (
      <Button variant="ghost" size="icon" disabled title="Loading..." data-testid="push-loading-btn">
        <Loader2 className="w-5 h-5 animate-spin" />
      </Button>
    );
  }

  if (permission === 'denied') {
    return (
      <Button 
        variant="ghost" 
        size="icon" 
        disabled 
        className="text-red-500" 
        title="Notifications Blocked - Check browser settings"
        data-testid="push-blocked-btn"
      >
        <BellOff className="w-5 h-5" />
      </Button>
    );
  }

  if (isSubscribed) {
    return (
      <Button 
        variant="ghost" 
        size="icon"
        onClick={sendTestNotification}
        className="text-green-600 relative"
        data-testid="push-subscribed-btn"
        title="Notifications Enabled - Click to test"
      >
        <BellRing className="w-5 h-5" />
        <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full"></span>
      </Button>
    );
  }

  return (
    <Button 
      variant="ghost" 
      size="icon"
      onClick={async () => {
        // Show a toast explaining what will happen
        toast.info('Requesting notification permission...');
        await subscribe();
      }}
      className="text-orange-500 hover:text-orange-600"
      data-testid="enable-push-btn"
      title="Enable Push Notifications"
    >
      <BellOff className="w-5 h-5" />
    </Button>
  );
};

export default PushNotificationManager;
